import {
  Button,
  ButtonLink,
  Divider,
  Heading,
  LayoutGridContainer,
  LayoutVerticalSpacer,
  TextField,
} from '@madpaws/design-system';
import { useFormikContext } from 'formik';
import { useRouter } from 'next/router';
import React from 'react';

import { PASSWORD_RECOVERY_PAGE_SLUG } from '~/common/constants/pageLayout';

import styles from './EmailLoginForm.module.css';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { useAuthenticationContext } from '../useAuthenticationContext';

import type { EmailLoginFormValues } from './EmailLoginDialog';
import type { ChangeEvent } from 'react';

type Props = {
  apiError?: string | null;
  isAPILoading?: boolean;
};

export const EmailLoginForm = ({ apiError, isAPILoading }: Props): React.ReactElement => {
  const {
    isSubmitting,
    submitForm,
    setFieldValue,
    values,
    isValid,
  } = useFormikContext<EmailLoginFormValues>();
  const { setEmailLoginDialogVisible } = useAuthenticationContext();

  const router = useRouter();

  const handleForgotPassword = (): void => {
    setEmailLoginDialogVisible(false);
    router.push(`${PASSWORD_RECOVERY_PAGE_SLUG}?email=${values.email}`);
  };

  const isSubmitButtonDisabled = isSubmitting || !isValid;

  const onTextFieldChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { name, value },
    } = event;

    setFieldValue(name, value);
  };

  return (
    <LayoutGridContainer rowGap="default">
      <Heading size="small2x">Log in to your account</Heading>
      <p>Enter the password for your account {values.email}</p>
      <LayoutVerticalSpacer>
        <ErrorBoundary fieldName="password">
          <TextField
            label="Password"
            name="password"
            onChange={onTextFieldChange}
            placeholder="Enter your password"
            type="password"
            value={values.password}
          />
        </ErrorBoundary>
        <div className={styles.forgotPasswordContainer}>
          <ButtonLink onClick={handleForgotPassword}>Forgot password</ButtonLink>
        </div>
        <Divider />
      </LayoutVerticalSpacer>
      <>{apiError && <p className={styles.error}>{apiError}</p>}</>
      <Button
        isDisabled={isAPILoading || isSubmitButtonDisabled}
        isFullBleed
        label="Login"
        onClick={submitForm}
      />
    </LayoutGridContainer>
  );
};
